'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketConfig } from '@morfi/backend-sdk-v2/client';
import type { MarketStatusString } from '@morfi/contract-sdk';
import { isValidMarketStatus } from '@morfi/contract-sdk';
import { useReducedMotion } from 'motion/react';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import type { ComponentProps, ComponentType } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ConnectedChartContainer } from '@/components/chart/connected-chart-container';
import { PaintedMarketShareDisplayProvider, useAuthoritativePaintedDisplayCandidate, usePaintedMarketShareStructure } from '@/components/chart/painted-market-share-display';
import { PrizePool, type PrizePoolPayoutEstimateView } from '@/components/chart/prize-pool';
import { APP_HEADER_STICKY_TOP_SIDEBAR_CLASS } from '@/components/layout/app-header-sticky-top';
import { EventTimeCard, type EventTimeCardProps } from '@/components/live/event-time-card';
import { BackerRewardsCardView, type BackerRewardsView } from '@/components/market/backer-rewards';
import { createBackerRewardsView, resolveBackerRewardsPlacement } from '@/components/market/backer-rewards/backer-rewards-placement';
import { ConnectedMarketOutcomesOverviewPanel } from '@/components/market/connected-market-outcomes-overview-panel';
import { MarketMobileTradeRoot, MarketMobileTradeStickyAnchor } from '@/components/market/floating-trade-button';
import { MarketIdentitySection } from '@/components/market/identity/market-identity-section';
// Modal/dialog components that only render their content behind user
// interaction (open state) — split into separate chunks via `next/dynamic`
// so their JS isn't shipped in the initial market detail bundle.
import { MarketDetailTabs } from '@/components/market/market-detail-tabs';
import { MarketHeader } from '@/components/market/market-header';
import { MarketHeaderSkeleton } from '@/components/market/market-header-skeleton';
import { MarketPositionPanel } from '@/components/market/market-position-panel';
import { ResolutionCard, type ResolutionCardState } from '@/components/market/resolution-card';
import { StageOverrideMount } from '@/components/market/stage-override-mount';
import { useSellDeeplinkPreset } from '@/components/market/use-sell-deeplink-preset';
import { DirectTokenPurchaseAction } from '@/components/onramp/direct-purchase/direct-token-purchase-action';
import { DemoNotFoundCard } from '@/components/shared/demo-not-found-card';
import type { ThesisDialog as ThesisDialogType } from '@/components/trade/thesis-dialog';
import type { MarketChartOpening } from '@/lib/chart/market-chart-opening';
import type { PaintedMarketShareStructure } from '@/lib/chart/painted-market-share-display';
import { shouldFireResolutionFireworks } from '@/lib/market/resolution-fireworks';
import { hasIndexedPrizePoolTotal } from '@/lib/market/resolution-hold';
import { selectMarketDetailOutcome } from '@/lib/market/select-market-detail-outcome';

// `loading` gives each lazy dialog its own boundary: without one, a first mount
// after a client navigation (e.g. a proposal handing off to this page) suspends
// to the route boundary and repaints the whole page as its loading skeleton.
const ThesisDialog: ComponentType<ComponentProps<typeof ThesisDialogType>> = dynamic(() => import('@/components/trade/thesis-dialog').then(mod => ({
  default: mod.ThesisDialog
})), {
  loading: () => null
});
// Client-only with its own empty fallback: it mounts with the page (inactive),
// so the chunk is already loaded when a resolution starts and its first load
// can never suspend the page into the route skeleton.
const MarketMigrationGlow: ComponentType<{
  active: boolean;
}> = dynamic(() => import('@/components/market/market-migration-glow').then(mod => ({
  default: mod.MarketMigrationGlow
})), {
  ssr: false,
  loading: () => null
});
function MarketResolutionGlow(t0) {
  const $ = _c(2);
  const {
    c
  } = t0;
  const candidate = useAuthoritativePaintedDisplayCandidate();
  const structure = usePaintedMarketShareStructure();
  const authoritative = candidate;
  const paintedAvailable = structure.availability === "available" || structure.availability === "degraded";
  const t1 = authoritative ? paintedAvailable && structure.thresholdReached : c.resolvingStep;
  let t2;
  if ($[0] !== t1) {
    t2 = <MarketMigrationGlow active={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}
import { type GetMarketRow, type GetPositionsRow, hashDiagnosticEntity, isMarketDetailDebugOverridden, type MarketPipelineStage, type MarketResolutionThresholdConsumers, marketResolutionView, type ProjectMarketResolutionAtResult, useActiveWallet, useAuth, useMarketConfig, useMarketResolutionThreshold } from '@morfi/backend-sdk-v2/client';
import { ConnectedMarketTradeForm } from '@/components/market/connected-market-trade-form';
import { GraduatingBanner } from '@/components/market/graduating-banner';
import { MarketDetailSidebar } from '@/components/market/market-detail-sidebar';
import { buildPayoutModel } from '@/components/market/prize-pool-payout-model';
import { TokenCta } from '@/components/market/token-cta-card';
import { SimilarMarketsSection } from '@/components/markets/similar-markets';
import { Panel } from '@/components/ui/panel';
import { onEvent } from '@/events';
import { useChartColors } from '@/hooks/chart/use-chart-colors';
import { useMarketDetailChartDefault } from '@/hooks/chart/use-market-detail-chart-default';
import { type OutcomeHueName, useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { useMarketLifecycleModalTrigger } from '@/hooks/market/use-market-lifecycle-modal-trigger';
import type { useMarketPositions, useOwnedMarketPositionScope } from '@/hooks/positions/use-market-positions';
import { usePrizePoolHoldings } from '@/hooks/positions/user-portfolio/use-prize-pool-holdings';
import { useBackerRewards } from '@/hooks/trading/use-backer-rewards';
import { useOutcomeConviction } from '@/hooks/trading/use-outcome-conviction';
import { usePrizePool } from '@/hooks/trading/use-prize-pool';
import { useRoutePerf } from '@/hooks/use-route-perf';
import { isResolvingStep, resolveResolutionCardState } from '@/lib/market/resolution-card-state';
import { type MarketDetailTab, resolveMarketDetailTab } from '@/lib/sdk/market-detail-tab-query';
import seoCopy from '@/lib/seo/copy';
import { logger } from '@/lib/telemetry/logger';
import { deriveOutcomeSpotPricesD9 } from '@/lib/trading/outcome-spot-price';
import { fireResolutionFireworks } from '@/lib/ui/confetti';
import { DETAIL_SIDEBAR_W_CLASS } from '@/lib/ui/layout';
import { cn } from '@/lib/ui/utils';
import { useDemoMode } from '@/providers/demo-mode-provider';
import { TradeExecutorProvider } from '@/providers/trade-executor-provider';
import { isPoolSnapshotUnavailable, useOnchainTradeData } from '@/queries/onchain-trade-data';
import { useChartSettingsStore } from '@/stores/chart-settings';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface MarketDetailLayoutProps {
  marketPubkey: string;
  /** The same completed DTO dehydrated by the route, available before effects. */
  initialMarket?: GetMarketRow | undefined;
  chartOpening?: MarketChartOpening | undefined;
  /** Outcome mints from the normal server-prefetched market detail DTO. */
  realtimeOutcomeMints: readonly string[];
  /** Server-computed lower bound shared with the dehydrated trade-tape query. */
  tradesFrom: string;
  initialOutcome?: string;
  /** Relative path this page canonicalises on — drives the in-page share
   *  URLs (header share, prize-claim share, resolution-hero share).
   *  Defaults to `/markets/{marketPubkey}`; the winners route passes
   *  `/winners/{mint}` so a shared winner links back to its canonical URL. */
  canonicalPath?: string;
  /** Linked event presentation context for a live market. Its presence is the
   * UI discriminator until the live-event SDK entity owns this data. */
  liveEvent?: Omit<EventTimeCardProps, 'variant'>;
}
export interface MarketDetailDataProps extends MarketDetailLayoutProps {
  marketData: GetMarketRow | undefined;
  marketIsPending: boolean;
  /** A resolution is landing: `marketData` still reads `trading` and only the
   *  resolution card moves on, until the settled page's data is in. */
  resolutionHolding: boolean;
}
interface MarketDetailViewProps extends MarketDetailDataProps {
  ownedPositionScope: ReturnType<typeof useOwnedMarketPositionScope>;
  marketPositions: ReturnType<typeof useMarketPositions>;
}

// ---------------------------------------------------------------------------
// Tabs + trade-form status type guard
// ---------------------------------------------------------------------------

type MarketSectionTab = MarketDetailTab;
function isValidTradeFormStatus(status: string): status is MarketStatusString {
  return isValidMarketStatus(status);
}
function startThesisHighlight(targetId: string): () => void {
  const FLASH_BASE = ['rounded-card', 'transition-shadow', 'duration-normal'];
  const FLASH_RING = ['ring-2', 'ring-button-primary-ring', 'ring-offset-2', 'ring-offset-background-secondary'];
  const FLASH_HOLD_MS = 500;
  const FLASH_FADE_MS = 250;
  let attempts = 0;
  const maxAttempts = 25;
  let animationFrameId: number | null = null;
  let removeRingTimer: ReturnType<typeof setTimeout> | null = null;
  let removeBaseTimer: ReturnType<typeof setTimeout> | null = null;
  let flashedEl: HTMLElement | null = null;
  const interval = setInterval(() => {
    attempts += 1;
    const el = document.getElementById(targetId);
    if (el !== null) {
      clearInterval(interval);
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      animationFrameId = window.requestAnimationFrame(() => {
        el.classList.add(...FLASH_BASE, ...FLASH_RING);
      });
      flashedEl = el;
      removeRingTimer = setTimeout(() => {
        el.classList.remove(...FLASH_RING);
      }, FLASH_HOLD_MS);
      removeBaseTimer = setTimeout(() => {
        el.classList.remove(...FLASH_BASE);
      }, FLASH_HOLD_MS + FLASH_FADE_MS);
    } else if (attempts >= maxAttempts) {
      clearInterval(interval);
    }
  }, 200);
  return (): void => {
    clearInterval(interval);
    if (removeRingTimer !== null) {
      clearTimeout(removeRingTimer);
    }
    if (removeBaseTimer !== null) {
      clearTimeout(removeBaseTimer);
    }
    if (animationFrameId !== null) {
      window.cancelAnimationFrame(animationFrameId);
    }
    if (flashedEl !== null) {
      flashedEl.classList.remove(...FLASH_BASE, ...FLASH_RING);
    }
  };
}

// ---------------------------------------------------------------------------
// Controller model
// ---------------------------------------------------------------------------

interface MarketCreatorSummary {
  userId: string | null;
  walletAddress: string;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
  avatarNsfw: boolean;
  avatarPreferences: {
    rasterise: boolean;
  };
}
interface TradeFormOutcome {
  index: number;
  label: string;
  mint: string;
  imageUrl: string | undefined;
  imageNsfw: boolean;
}
interface MobileTradeOutcome {
  index: number;
  title: string;
  mint: string;
  imageUrl: string | undefined;
  imageNsfw: boolean;
}
interface ResolvedPanelOutcome {
  outcomeIndex: number;
  label: string;
  ticker: string | null;
  mint: string | null;
  imageUrl: string | null;
  imageNsfw: boolean;
  convictionAtResolution: string | null;
  convictionAnchor: string | null;
  peakAtResolution: string | null;
  convictionPeak: string | null;
  volumeAtResolutionD6: string | null;
  volumeTotalD6: string | null;
}
export interface MarketDetailModel {
  readonly marketConfig: MarketConfig | undefined;
  marketPubkey: string;
  tradesFrom: string;
  shareCanonicalPath: string;
  liveEvent: MarketDetailLayoutProps['liveEvent'];
  isDemoMode: boolean;
  marketIsPending: boolean;
  market: GetMarketRow | undefined;
  marketRecord: Record<string, unknown> | undefined;
  outcomes: GetMarketRow['outcomes'] | undefined;
  realtimeOutcomeMints: readonly string[];
  marketStatus: string;
  userPubkey: string | undefined;
  selectedOutcomeIndex: number | null;
  hasHoldings: boolean;
  holdingsQuerySettled: boolean;
  positionRows: readonly GetPositionsRow[];
  hasBackerRewards: boolean;
  backerRewards: BackerRewardsView;
  backerRewardPool: ReturnType<typeof useBackerRewards>['rewardPool'];
  prizePool: ReturnType<typeof usePrizePool>;
  prizePoolTotalUnavailable: boolean;
  payoutEstimates: readonly PrizePoolPayoutEstimateView[] | null;
  payoutEstimatesPending: boolean;
  onchainTradeData: ReturnType<typeof useOnchainTradeData>;
  poolSnapshotUnavailable: boolean;
  liveConviction: ReturnType<typeof useOutcomeConviction>;
  pipelineStage: MarketPipelineStage;
  consensusThreshold: number | null;
  thresholdConsumers: MarketResolutionThresholdConsumers;
  /** See {@link MarketDetailDataProps.resolutionHolding}. */
  resolutionHolding: boolean;
  /** The resolving step (see `isResolvingStep`): the resolution glow plays. */
  resolvingStep: boolean;
  /** Outcome projected to cross the threshold first, with its fixed crossing time. */
  projectedWinner: ProjectMarketResolutionAtResult | null;
  /** Live distance from that outcome to the line; `null` when nothing is projected. */
  projectedGap: number | null;
  winnerOutcomeIdx: number | null;
  isPostResolution: boolean;
  winnerName: string;
  winnerTicker: string | null;
  winnerMint: string;
  winnerImageUrl: string | null;
  winnerImageNsfw: boolean;
  winningOutcomeLabel: string | null;
  creator: MarketCreatorSummary | null;
  tradeFormOutcomes: TradeFormOutcome[];
  outcomeSpotPriceD9ByIndex: ReadonlyMap<number, string>;
  mobileTradeOutcomes: MobileTradeOutcome[];
  resolvedPanelOutcomes: ResolvedPanelOutcome[];
  outcomeColorMap: Map<number, string>;
  outcomeHueByIndex: Map<number, OutcomeHueName>;
  imageUrlByIndex: Map<number, string>;
  imageNsfwByIndex: Map<number, boolean>;
  handleOutcomeChange: (index: number) => void;
  handleChartOutcomeChange: (index: number) => void;
  activeMarketSectionTab: MarketSectionTab;
  setMarketSectionTabOverride: (tab: MarketSectionTab) => void;
  tabSortParam: string | null;
  prefersReducedMotion: boolean | null;
}

// ---------------------------------------------------------------------------
// Controller hook — all data, derivation, effects, and handlers live here so
// the render component stays under the no-giant-component threshold.
// ---------------------------------------------------------------------------

function nullableStringField(record: Record<string, unknown> | undefined, key: string): string | null {
  const value = record?.[key];
  return typeof value === 'string' ? value : null;
}
function nullableNumberField(record: Record<string, unknown> | undefined, key: string): number | null {
  const value = record?.[key];
  return value === null || value === undefined ? null : Number(value);
}
function createMarketCreator(market: GetMarketRow | undefined): MarketCreatorSummary | null {
  const profile = market?.creatorProfile;
  if (profile === null || profile === undefined) return null;
  return {
    userId: profile.userId,
    walletAddress: profile.walletAddress,
    displayName: profile.displayName,
    handle: profile.handle,
    avatarUrl: profile.avatarUrl,
    avatarNsfw: profile.avatarNsfw ?? false,
    // ALLOW-RASTER-LITERAL: object default for a missing preference; rasterisation is opt-in.
    avatarPreferences: profile.avatarPreferences ?? {
      rasterise: false
    }
  };
}
interface OutcomePresentation {
  readonly imageUrlByIndex: Map<number, string>;
  readonly imageNsfwByIndex: Map<number, boolean>;
  readonly winnerName: string;
  readonly winnerTicker: string | null;
  readonly winnerMint: string;
  readonly winnerImageUrl: string | null;
  readonly winnerImageNsfw: boolean;
  readonly winningOutcomeLabel: string | null;
  readonly tradeFormOutcomes: TradeFormOutcome[];
  readonly mobileTradeOutcomes: MobileTradeOutcome[];
  readonly resolvedPanelOutcomes: ResolvedPanelOutcome[];
}
function outcomeRecord(outcome: GetMarketRow['outcomes'][number]): Record<string, unknown> {
  return Object.fromEntries(Object.entries(outcome));
}
function createResolvedPanelOutcome(outcome: GetMarketRow['outcomes'][number]): ResolvedPanelOutcome {
  const record = outcomeRecord(outcome);
  return {
    outcomeIndex: Number(outcome.outcomeIndex),
    label: String(outcome.label ?? ''),
    ticker: nullableStringField(record, 'ticker'),
    mint: nullableStringField(record, 'mint'),
    imageUrl: nullableStringField(record, 'imageUrl'),
    imageNsfw: record.imageNsfw === true,
    convictionAtResolution: nullableStringField(record, 'convictionAtResolution'),
    convictionAnchor: nullableStringField(record, 'convictionAnchor'),
    peakAtResolution: nullableStringField(record, 'peakAtResolution'),
    convictionPeak: nullableStringField(record, 'convictionPeak'),
    volumeAtResolutionD6: nullableStringField(record, 'volumeAtResolution'),
    volumeTotalD6: nullableStringField(record, 'volumeTotal')
  };
}
function buildOutcomePresentation(outcomes: GetMarketRow['outcomes'] | undefined, winnerOutcomeIdx: number | null): OutcomePresentation {
  const rows = outcomes ?? [];
  const imageUrlByIndex = new Map<number, string>();
  const imageNsfwByIndex = new Map<number, boolean>();
  for (const outcome of rows) {
    const record = outcomeRecord(outcome);
    const imageUrl = String(record.imageUrl ?? '');
    if (imageUrl !== '') imageUrlByIndex.set(Number(outcome.outcomeIndex), imageUrl);
    imageNsfwByIndex.set(Number(outcome.outcomeIndex), record.imageNsfw === true);
  }
  const winner = winnerOutcomeIdx === null ? undefined : rows.find(outcome => Number(outcome.outcomeIndex) === winnerOutcomeIdx);
  const winnerRecord = winner === undefined ? undefined : outcomeRecord(winner);
  const tradeFormOutcomes = rows.map(outcome => ({
    index: Number(outcome.outcomeIndex),
    label: outcome.label,
    mint: String(outcome.mint ?? ''),
    imageUrl: imageUrlByIndex.get(Number(outcome.outcomeIndex)),
    imageNsfw: imageNsfwByIndex.get(Number(outcome.outcomeIndex)) === true
  }));
  return {
    imageUrlByIndex,
    imageNsfwByIndex,
    winnerName: winner?.label ?? '',
    winnerTicker: nullableStringField(winnerRecord, 'ticker'),
    winnerMint: String(winnerRecord?.mint ?? ''),
    winnerImageUrl: winnerOutcomeIdx === null ? null : imageUrlByIndex.get(winnerOutcomeIdx) ?? null,
    winnerImageNsfw: winnerOutcomeIdx !== null && imageNsfwByIndex.get(winnerOutcomeIdx) === true,
    winningOutcomeLabel: winner?.label ?? null,
    tradeFormOutcomes,
    mobileTradeOutcomes: tradeFormOutcomes.map(outcome => ({
      index: outcome.index,
      title: outcome.label,
      mint: outcome.mint,
      imageUrl: outcome.imageUrl,
      imageNsfw: outcome.imageNsfw
    })),
    resolvedPanelOutcomes: rows.map(createResolvedPanelOutcome)
  };
}
function buildOutcomeColorMap(inputs: ReadonlyArray<{
  readonly index: number;
}>, colors: ReadonlyArray<string>): Map<number, string> {
  return new Map(inputs.map((input, index) => [input.index, colors[index] ?? '']));
}
function useInitialOutcomeSelection(args) {
  const $ = _c(6);
  const setSelectedOutcome = useChartSettingsStore(_temp);
  const seededRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== args.initialOutcome || $[1] !== args.marketPubkey || $[2] !== args.outcomes || $[3] !== setSelectedOutcome) {
    t0 = () => {
      if (seededRef.current || args.outcomes === undefined) {
        return;
      }
      const match = args.outcomes.find(outcome => outcome.label.toLowerCase() === args.initialOutcome?.toLowerCase());
      if (match !== undefined) {
        setSelectedOutcome(args.marketPubkey, Number(match.outcomeIndex));
      }
      seededRef.current = true;
    };
    t1 = [args.initialOutcome, args.marketPubkey, args.outcomes, setSelectedOutcome];
    $[0] = args.initialOutcome;
    $[1] = args.marketPubkey;
    $[2] = args.outcomes;
    $[3] = setSelectedOutcome;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
}
function _temp(state) {
  return state.setSelectedOutcome;
}
function holdingsAreSettled(args: {
  readonly isAuthLoading: boolean;
  readonly isAuthenticated: boolean;
  readonly positionRowCount: number;
  readonly positionsPending: boolean;
}): boolean {
  if (args.isAuthLoading) return false;
  if (!args.isAuthenticated) return true;
  return args.positionRowCount > 0 || !args.positionsPending;
}
function outcomeSpotPrices(onchainTradeData: ReturnType<typeof useOnchainTradeData>, poolSnapshotUnavailable: boolean): ReadonlyMap<number, string> {
  if (onchainTradeData.data === undefined) return new Map();
  return new Map(deriveOutcomeSpotPricesD9(onchainTradeData.data, poolSnapshotUnavailable).map(spot => [spot.outcomeIndex, spot.priceD9]));
}
function useMarketSectionSelection(args) {
  const $ = _c(11);
  const [selection, setSelection] = useState();
  const override = selection?.marketPubkey === args.marketPubkey && selection.tabParam === args.tabParam ? selection.tab : undefined;
  let t0;
  if ($[0] !== args.hasBackerRewards || $[1] !== args.marketStatus || $[2] !== args.tabParam || $[3] !== override) {
    t0 = resolveMarketDetailTab(args.tabParam, args.marketStatus, override, args.hasBackerRewards);
    $[0] = args.hasBackerRewards;
    $[1] = args.marketStatus;
    $[2] = args.tabParam;
    $[3] = override;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== args.marketPubkey || $[6] !== args.tabParam) {
    t1 = tab => {
      setSelection({
        marketPubkey: args.marketPubkey,
        tabParam: args.tabParam,
        tab
      });
    };
    $[5] = args.marketPubkey;
    $[6] = args.tabParam;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== t0 || $[9] !== t1) {
    t2 = {
      activeTab: t0,
      setOverride: t1
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
function useThesisHighlight(tabParam, thesisParam) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== tabParam || $[1] !== thesisParam) {
    t0 = () => {
      if (tabParam !== "debate" || thesisParam === null) {
        return;
      }
      return startThesisHighlight(`thesis-${thesisParam}`);
    };
    t1 = [tabParam, thesisParam];
    $[0] = tabParam;
    $[1] = thesisParam;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}
function useResolutionFireworks(marketStatus, marketIsDebugOverridden) {
  const $ = _c(4);
  const previousMarketStatusRef = useRef(null);
  let t0;
  let t1;
  if ($[0] !== marketIsDebugOverridden || $[1] !== marketStatus) {
    t0 = () => {
      const previous = previousMarketStatusRef.current;
      previousMarketStatusRef.current = marketStatus;
      if (shouldFireResolutionFireworks(previous, marketStatus, marketIsDebugOverridden)) {
        fireResolutionFireworks();
      }
    };
    t1 = [marketStatus, marketIsDebugOverridden];
    $[0] = marketIsDebugOverridden;
    $[1] = marketStatus;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}
function useMarketDetailEffects(args) {
  const $ = _c(13);
  let t0;
  let t1;
  if ($[0] !== args.marketPubkey || $[1] !== args.positionsError) {
    t0 = () => {
      if (args.positionsError) {
        logger.warn("market.position_history_failed", {
          market: args.marketPubkey
        });
      }
    };
    t1 = [args.marketPubkey, args.positionsError];
    $[0] = args.marketPubkey;
    $[1] = args.positionsError;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] !== args.hasHoldings || $[5] !== args.marketPubkey || $[6] !== args.marketStatus || $[7] !== args.marketTitle) {
    t2 = () => {
      if (args.marketTitle === undefined) {
        return;
      }
      onEvent("market_detail_viewed", {
        marketPubkey: args.marketPubkey,
        marketStatus: args.marketStatus,
        hasPositions: args.hasHoldings
      });
    };
    t3 = [args.hasHoldings, args.marketPubkey, args.marketStatus, args.marketTitle];
    $[4] = args.hasHoldings;
    $[5] = args.marketPubkey;
    $[6] = args.marketStatus;
    $[7] = args.marketTitle;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[10] !== args.marketTitle) {
    t4 = () => {
      if (args.marketTitle !== undefined) {
        document.title = `${args.marketTitle}${seoCopy.site.titleSuffix}`;
      }
    };
    t5 = [args.marketTitle];
    $[10] = args.marketTitle;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  useEffect(t4, t5);
}
function resolveMarketClaimState(market: GetMarketRow | undefined, marketStatus: string): {
  readonly record: Record<string, unknown> | undefined;
  readonly claimTotalClaimable: string | null;
  readonly winnerOutcomeIdx: number | null;
  readonly marketIsResolved: boolean;
  readonly indexedPrizePoolTotalAvailable: boolean;
} {
  const record = market as Record<string, unknown> | undefined;
  const claimTotalClaimable = nullableStringField(record, 'claimTotalClaimable');
  return {
    record,
    claimTotalClaimable,
    winnerOutcomeIdx: nullableNumberField(record, 'winnerOutcomeIndex'),
    marketIsResolved: marketStatus === 'resolved' || marketStatus === 'graduated',
    indexedPrizePoolTotalAvailable: hasIndexedPrizePoolTotal(claimTotalClaimable)
  };
}
function useMarketDetailLayout(t0) {
  const $ = _c(143);
  const {
    marketPubkey,
    realtimeOutcomeMints,
    tradesFrom,
    initialOutcome,
    canonicalPath,
    marketData,
    marketIsPending,
    resolutionHolding,
    liveEvent,
    ownedPositionScope,
    marketPositions
  } = t0;
  const shareCanonicalPath = canonicalPath ?? `/markets/${marketPubkey}`;
  const {
    user,
    isAuthenticated,
    isLoading: isAuthLoading
  } = useAuth();
  const {
    isDemoMode
  } = useDemoMode();
  const {
    activeWallet
  } = useActiveWallet();
  const userPubkey = activeWallet?.address ?? undefined;
  const marketOutcomes = marketData?.outcomes ?? [];
  const outcomeMints = marketOutcomes.map(_temp2);
  const t1 = user?.id ?? "";
  let t2;
  if ($[0] !== marketPositions.rows || $[1] !== marketPubkey || $[2] !== ownedPositionScope.walletPubkeys || $[3] !== t1) {
    t2 = {
      userId: t1,
      wallets: ownedPositionScope.walletPubkeys,
      market: marketPubkey,
      indexedRows: marketPositions.rows
    };
    $[0] = marketPositions.rows;
    $[1] = marketPubkey;
    $[2] = ownedPositionScope.walletPubkeys;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const payoutHoldings = usePrizePoolHoldings(t2);
  let t3;
  if ($[5] !== isAuthenticated || $[6] !== marketData || $[7] !== marketIsPending || $[8] !== marketPositions.isPending || $[9] !== ownedPositionScope.isLoading || $[10] !== payoutHoldings) {
    t3 = buildPayoutModel({
      market: marketData,
      positionRows: payoutHoldings,
      isAuthenticated,
      positionScopeLoading: ownedPositionScope.isLoading,
      marketIsPending,
      positionsPending: marketPositions.isPending
    });
    $[5] = isAuthenticated;
    $[6] = marketData;
    $[7] = marketIsPending;
    $[8] = marketPositions.isPending;
    $[9] = ownedPositionScope.isLoading;
    $[10] = payoutHoldings;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const payout = t3;
  const outcomeCount = marketOutcomes.length;
  const onchainTradeData = useOnchainTradeData(marketPubkey, outcomeCount, outcomeMints.map(_temp3), marketData);
  const poolSnapshotUnavailable = isPoolSnapshotUnavailable(onchainTradeData);
  const market = marketData;
  const outcomes = marketData?.outcomes;
  useSellDeeplinkPreset(marketPubkey, outcomes !== undefined);
  let t4;
  if ($[12] !== outcomes) {
    t4 = outcomes?.map(_temp4);
    $[12] = outcomes;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const t5 = marketData?.status;
  const t6 = marketData?.resolvedAt;
  const t7 = marketData?.convictionUpdatedAt;
  let t8;
  if ($[14] !== outcomeCount || $[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = {
      status: t5,
      resolvedAt: t6,
      convictionUpdatedAt: t7,
      outcomeCount
    };
    $[14] = outcomeCount;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const liveConviction = useOutcomeConviction(t4, t8);
  let t9;
  if ($[19] !== marketData) {
    t9 = createMarketCreator(marketData);
    $[19] = marketData;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const creator = t9;
  let t10;
  if ($[21] !== marketPubkey) {
    t10 = s => s.selectedOutcome?.[marketPubkey] ?? null;
    $[21] = marketPubkey;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const selectedOutcomeIndex = useChartSettingsStore(t10);
  const toggleOutcomeFilter = useChartSettingsStore(_temp5);
  const setSelectedOutcome = useChartSettingsStore(_temp6);
  const setHiddenOutcomes = useChartSettingsStore(_temp7);
  const searchParams = useSearchParams();
  let t11;
  if ($[23] !== initialOutcome || $[24] !== marketPubkey || $[25] !== outcomes) {
    t11 = {
      initialOutcome,
      outcomes,
      marketPubkey
    };
    $[23] = initialOutcome;
    $[24] = marketPubkey;
    $[25] = outcomes;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  useInitialOutcomeSelection(t11);
  let t12;
  if ($[27] !== marketPubkey || $[28] !== outcomes || $[29] !== setHiddenOutcomes || $[30] !== setSelectedOutcome) {
    t12 = index_0 => {
      setHiddenOutcomes(marketPubkey, []);
      selectMarketDetailOutcome({
        marketPubkey,
        index: index_0,
        outcomes,
        setSelectedOutcome
      });
    };
    $[27] = marketPubkey;
    $[28] = outcomes;
    $[29] = setHiddenOutcomes;
    $[30] = setSelectedOutcome;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const handleOutcomeChange = t12;
  let t13;
  if ($[32] !== marketPubkey || $[33] !== outcomes || $[34] !== setSelectedOutcome || $[35] !== toggleOutcomeFilter) {
    t13 = index_1 => {
      const settings = useChartSettingsStore.getState();
      if (settings.chartMode === "live" && settings.renderMode === "candle") {
        selectMarketDetailOutcome({
          marketPubkey,
          index: index_1,
          outcomes,
          setSelectedOutcome
        });
        return;
      }
      selectMarketDetailOutcome({
        marketPubkey,
        index: index_1,
        outcomes,
        setSelectedOutcome: (marketPubkey_0, outcomeIndex) => toggleOutcomeFilter(marketPubkey_0, outcomeIndex, outcomes?.map(_temp8) ?? [])
      });
    };
    $[32] = marketPubkey;
    $[33] = outcomes;
    $[34] = setSelectedOutcome;
    $[35] = toggleOutcomeFilter;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  const handleChartOutcomeChange = t13;
  let t14;
  if ($[37] !== ownedPositionScope.activeWalletPubkey) {
    t14 = position => position.wallet === ownedPositionScope.activeWalletPubkey;
    $[37] = ownedPositionScope.activeWalletPubkey;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const activeWalletPositions = marketPositions.visibleRows.filter(t14);
  const hasHoldings = activeWalletPositions.length > 0;
  const holdings = activeWalletPositions.map(_temp9);
  let t15;
  if ($[39] !== isAuthLoading || $[40] !== isAuthenticated || $[41] !== marketPositions.isPending || $[42] !== marketPositions.rows.length) {
    t15 = holdingsAreSettled({
      isAuthLoading,
      isAuthenticated,
      positionRowCount: marketPositions.rows.length,
      positionsPending: marketPositions.isPending
    });
    $[39] = isAuthLoading;
    $[40] = isAuthenticated;
    $[41] = marketPositions.isPending;
    $[42] = marketPositions.rows.length;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const holdingsQuerySettled = t15;
  const marketStatus = String(market?.status ?? "trading");
  let t16;
  if ($[44] !== marketPubkey || $[45] !== realtimeOutcomeMints) {
    t16 = isMarketDetailDebugOverridden({
      pubkey: marketPubkey,
      mints: realtimeOutcomeMints
    });
    $[44] = marketPubkey;
    $[45] = realtimeOutcomeMints;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const marketIsDebugOverridden = t16;
  let t17;
  if ($[47] !== searchParams) {
    t17 = searchParams.get("tab");
    $[47] = searchParams;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  const tabParam = t17;
  let t18;
  if ($[49] !== searchParams) {
    t18 = searchParams.get("sort");
    $[49] = searchParams;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  const tabSortParam = t18;
  const prefersReducedMotion = useReducedMotion();
  const hasBackerRewards = market != null && market.marketType !== "featured";
  let t19;
  if ($[51] !== hasBackerRewards || $[52] !== marketPubkey || $[53] !== marketStatus || $[54] !== tabParam) {
    t19 = {
      marketPubkey,
      tabParam,
      marketStatus,
      hasBackerRewards
    };
    $[51] = hasBackerRewards;
    $[52] = marketPubkey;
    $[53] = marketStatus;
    $[54] = tabParam;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  const marketSection = useMarketSectionSelection(t19);
  let t20;
  if ($[56] !== searchParams) {
    t20 = searchParams.get("thesis");
    $[56] = searchParams;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  const thesisParam = t20;
  useThesisHighlight(tabParam, thesisParam);
  useResolutionFireworks(marketStatus, marketIsDebugOverridden);
  const claimState = resolveMarketClaimState(market, marketStatus);
  const {
    record: marketRecord,
    claimTotalClaimable,
    winnerOutcomeIdx,
    marketIsResolved,
    indexedPrizePoolTotalAvailable
  } = claimState;
  const prizePoolTotalUnavailable = marketIsResolved && !indexedPrizePoolTotalAvailable;
  useEffect(() => {
    if (prizePoolTotalUnavailable) {
      logger.warn("market.prize_pool_resolution_snapshot_failed", {
        marketHash: hashDiagnosticEntity(marketPubkey),
        source: "indexed_resolution"
      });
    }
  }, [marketPubkey, prizePoolTotalUnavailable]);
  const prizePool = usePrizePool({
    marketPubkey,
    marketStatus,
    winnerOutcomeIndex: winnerOutcomeIdx,
    prizePoolRaw: market?.prizePool ?? null,
    claimTotalClaimableRaw: claimTotalClaimable,
    buyBurnPoolRaw: nullableStringField(marketRecord, "buyBurnPool"),
    holdings: holdings.map(_temp10)
  });
  const marketConfig = useMarketConfig(market?.marketConfig);
  const t21 = market?.status ?? "created";
  const t22 = Number(market?.outcomeCount ?? 0);
  const t23 = market?.tradingStartedAt ?? null;
  const t24 = market?.resolvedAt ?? null;
  let t25;
  if ($[58] !== marketConfig || $[59] !== marketPubkey || $[60] !== t21 || $[61] !== t22 || $[62] !== t23 || $[63] !== t24) {
    t25 = {
      marketPubkey,
      config: marketConfig,
      status: t21,
      outcomeCount: t22,
      tradingStartedAt: t23,
      resolvedAt: t24
    };
    $[58] = marketConfig;
    $[59] = marketPubkey;
    $[60] = t21;
    $[61] = t22;
    $[62] = t23;
    $[63] = t24;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  const resolutionThreshold = useMarketResolutionThreshold(t25);
  const resolutionView = marketResolutionView({
    status: marketStatus,
    liveConviction,
    resolutionThreshold,
    showGoalPercent: liveEvent === undefined,
    config: marketConfig,
    outcomeCount: Number(market?.outcomeCount ?? 0),
    tradingStartedAt: market?.tradingStartedAt ?? null,
    convictionUpdatedAt: market?.convictionUpdatedAt ?? undefined,
    retentionPerSecondPpb: marketConfig?.convictionRetentionPerSecondPpb,
    outcomes
  });
  const thresholdConsumers = resolutionView.consumers;
  const consensusThreshold = thresholdConsumers.resolutionCardThreshold;
  const pipelineStage = resolutionView.pipelineStage;
  const outcomePresentation = buildOutcomePresentation(outcomes, winnerOutcomeIdx);
  const {
    imageUrlByIndex,
    imageNsfwByIndex,
    winnerName,
    winnerTicker,
    winnerMint,
    winnerImageUrl,
    winnerImageNsfw,
    winningOutcomeLabel,
    tradeFormOutcomes,
    mobileTradeOutcomes,
    resolvedPanelOutcomes
  } = outcomePresentation;
  const isPostResolution = marketStatus === "resolved" || marketStatus === "graduated";
  let t26;
  if ($[65] !== hasBackerRewards || $[66] !== marketPubkey || $[67] !== marketStatus) {
    t26 = {
      marketPubkey,
      marketStatus,
      enabled: hasBackerRewards
    };
    $[65] = hasBackerRewards;
    $[66] = marketPubkey;
    $[67] = marketStatus;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  const rewards = useBackerRewards(t26);
  let t27;
  if ($[69] !== rewards) {
    t27 = createBackerRewardsView(rewards);
    $[69] = rewards;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  const backerRewards = t27;
  useMarketLifecycleModalTrigger({
    marketPubkey,
    marketStatus: market === undefined ? null : marketStatus,
    winnerOutcomeIndex: winnerOutcomeIdx,
    resolvedAtIso: typeof market?.resolvedAt === "string" ? market.resolvedAt : null,
    holdings,
    isBacker: rewards.isBacker,
    isAuthenticated,
    viewerId: user?.id,
    isDemoMode,
    walletAddress: userPubkey ?? null,
    claim: {
      claimable: prizePool.isWinner,
      finalized: prizePool.locked,
      claimed: prizePool.hasClaimed
    },
    winningOutcomeLabel: winnerName !== "" ? winnerName : null,
    winningOutcomeImageUrl: winnerImageUrl,
    winningOutcomeImageNsfw: winnerImageNsfw
  });
  const outcomeSpotPriceD9ByIndex = outcomeSpotPrices(onchainTradeData, poolSnapshotUnavailable);
  const outcomeColorInputs = tradeFormOutcomes.map(_temp11);
  const chartColors = useChartColors(outcomeColorInputs, undefined, marketPubkey);
  let t28;
  if ($[71] !== chartColors || $[72] !== outcomeColorInputs) {
    t28 = buildOutcomeColorMap(outcomeColorInputs, chartColors);
    $[71] = chartColors;
    $[72] = outcomeColorInputs;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  const outcomeColorMap = t28;
  let t29;
  if ($[74] !== marketPubkey) {
    t29 = {
      marketPubkey
    };
    $[74] = marketPubkey;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  const outcomeHueClasses = useOutcomeColor(outcomeColorInputs, t29);
  let outcomeHueByIndex;
  if ($[76] !== outcomeColorInputs || $[77] !== outcomeHueClasses) {
    outcomeHueByIndex = new Map();
    outcomeColorInputs.forEach((input, i) => {
      const hue = outcomeHueClasses[i]?.hue;
      if (hue !== undefined) {
        outcomeHueByIndex.set(input.index, hue);
      }
    });
    $[76] = outcomeColorInputs;
    $[77] = outcomeHueClasses;
    $[78] = outcomeHueByIndex;
  } else {
    outcomeHueByIndex = $[78];
  }
  const projectedWinner = resolutionView.projected;
  const projectedGap = resolutionView.gap;
  const t30 = market?.title;
  let t31;
  if ($[79] !== hasHoldings || $[80] !== marketPositions.isError || $[81] !== marketPubkey || $[82] !== marketStatus || $[83] !== t30) {
    t31 = {
      marketPubkey,
      marketTitle: t30,
      marketStatus,
      hasHoldings,
      positionsError: marketPositions.isError
    };
    $[79] = hasHoldings;
    $[80] = marketPositions.isError;
    $[81] = marketPubkey;
    $[82] = marketStatus;
    $[83] = t30;
    $[84] = t31;
  } else {
    t31 = $[84];
  }
  useMarketDetailEffects(t31);
  const t32 = rewards.rewardPool;
  const t33 = payout.estimates;
  const t34 = payout.pending;
  const t35 = isResolvingStep({
    marketStatus,
    pipelineStage,
    resolutionHolding
  });
  let t36;
  if ($[85] !== activeWalletPositions || $[86] !== backerRewards || $[87] !== consensusThreshold || $[88] !== creator || $[89] !== handleChartOutcomeChange || $[90] !== handleOutcomeChange || $[91] !== hasBackerRewards || $[92] !== hasHoldings || $[93] !== holdingsQuerySettled || $[94] !== imageNsfwByIndex || $[95] !== imageUrlByIndex || $[96] !== isDemoMode || $[97] !== isPostResolution || $[98] !== liveConviction || $[99] !== liveEvent || $[100] !== market || $[101] !== marketConfig || $[102] !== marketIsPending || $[103] !== marketPubkey || $[104] !== marketRecord || $[105] !== marketSection.activeTab || $[106] !== marketSection.setOverride || $[107] !== marketStatus || $[108] !== mobileTradeOutcomes || $[109] !== onchainTradeData || $[110] !== outcomeColorMap || $[111] !== outcomeHueByIndex || $[112] !== outcomeSpotPriceD9ByIndex || $[113] !== outcomes || $[114] !== payout.estimates || $[115] !== payout.pending || $[116] !== pipelineStage || $[117] !== poolSnapshotUnavailable || $[118] !== prefersReducedMotion || $[119] !== prizePool || $[120] !== prizePoolTotalUnavailable || $[121] !== projectedGap || $[122] !== projectedWinner || $[123] !== realtimeOutcomeMints || $[124] !== resolutionHolding || $[125] !== resolvedPanelOutcomes || $[126] !== rewards.rewardPool || $[127] !== selectedOutcomeIndex || $[128] !== shareCanonicalPath || $[129] !== t35 || $[130] !== tabSortParam || $[131] !== thresholdConsumers || $[132] !== tradeFormOutcomes || $[133] !== tradesFrom || $[134] !== userPubkey || $[135] !== winnerImageNsfw || $[136] !== winnerImageUrl || $[137] !== winnerMint || $[138] !== winnerName || $[139] !== winnerOutcomeIdx || $[140] !== winnerTicker || $[141] !== winningOutcomeLabel) {
    t36 = {
      marketConfig,
      marketPubkey,
      tradesFrom,
      shareCanonicalPath,
      liveEvent,
      isDemoMode,
      marketIsPending,
      market,
      marketRecord,
      outcomes,
      realtimeOutcomeMints,
      marketStatus,
      selectedOutcomeIndex,
      userPubkey,
      hasHoldings,
      holdingsQuerySettled,
      positionRows: activeWalletPositions,
      hasBackerRewards,
      backerRewards,
      backerRewardPool: t32,
      prizePool,
      prizePoolTotalUnavailable,
      payoutEstimates: t33,
      payoutEstimatesPending: t34,
      onchainTradeData,
      poolSnapshotUnavailable,
      liveConviction,
      pipelineStage,
      consensusThreshold,
      thresholdConsumers,
      resolutionHolding,
      resolvingStep: t35,
      projectedWinner,
      projectedGap,
      winnerOutcomeIdx,
      isPostResolution,
      winnerName,
      winnerTicker,
      winnerMint,
      winnerImageUrl,
      winnerImageNsfw,
      winningOutcomeLabel,
      creator,
      tradeFormOutcomes,
      outcomeSpotPriceD9ByIndex,
      mobileTradeOutcomes,
      resolvedPanelOutcomes,
      outcomeColorMap,
      outcomeHueByIndex,
      imageUrlByIndex,
      imageNsfwByIndex,
      handleOutcomeChange,
      handleChartOutcomeChange,
      activeMarketSectionTab: marketSection.activeTab,
      setMarketSectionTabOverride: marketSection.setOverride,
      tabSortParam,
      prefersReducedMotion
    };
    $[85] = activeWalletPositions;
    $[86] = backerRewards;
    $[87] = consensusThreshold;
    $[88] = creator;
    $[89] = handleChartOutcomeChange;
    $[90] = handleOutcomeChange;
    $[91] = hasBackerRewards;
    $[92] = hasHoldings;
    $[93] = holdingsQuerySettled;
    $[94] = imageNsfwByIndex;
    $[95] = imageUrlByIndex;
    $[96] = isDemoMode;
    $[97] = isPostResolution;
    $[98] = liveConviction;
    $[99] = liveEvent;
    $[100] = market;
    $[101] = marketConfig;
    $[102] = marketIsPending;
    $[103] = marketPubkey;
    $[104] = marketRecord;
    $[105] = marketSection.activeTab;
    $[106] = marketSection.setOverride;
    $[107] = marketStatus;
    $[108] = mobileTradeOutcomes;
    $[109] = onchainTradeData;
    $[110] = outcomeColorMap;
    $[111] = outcomeHueByIndex;
    $[112] = outcomeSpotPriceD9ByIndex;
    $[113] = outcomes;
    $[114] = payout.estimates;
    $[115] = payout.pending;
    $[116] = pipelineStage;
    $[117] = poolSnapshotUnavailable;
    $[118] = prefersReducedMotion;
    $[119] = prizePool;
    $[120] = prizePoolTotalUnavailable;
    $[121] = projectedGap;
    $[122] = projectedWinner;
    $[123] = realtimeOutcomeMints;
    $[124] = resolutionHolding;
    $[125] = resolvedPanelOutcomes;
    $[126] = rewards.rewardPool;
    $[127] = selectedOutcomeIndex;
    $[128] = shareCanonicalPath;
    $[129] = t35;
    $[130] = tabSortParam;
    $[131] = thresholdConsumers;
    $[132] = tradeFormOutcomes;
    $[133] = tradesFrom;
    $[134] = userPubkey;
    $[135] = winnerImageNsfw;
    $[136] = winnerImageUrl;
    $[137] = winnerMint;
    $[138] = winnerName;
    $[139] = winnerOutcomeIdx;
    $[140] = winnerTicker;
    $[141] = winningOutcomeLabel;
    $[142] = t36;
  } else {
    t36 = $[142];
  }
  return t36;
}

// ---------------------------------------------------------------------------
// Resolving banner — confirming / post-resolution / optimistic / close-to
// (mutually exclusive with the ResolvedPanel, which owns the winner-known case)
// ---------------------------------------------------------------------------
function _temp11(o_1) {
  return {
    title: o_1.label,
    index: o_1.index
  };
}
function _temp10(h) {
  return {
    outcomeIndex: h.outcomeIndex,
    balance: h.balance,
    claimRightsBalanceD6: h.claimRightsBalanceD6,
    prizeClaimableD6: h.prizeClaimableD6,
    prizeClaimedD6: h.prizeClaimedD6,
    prizeState: h.prizeState
  };
}
function _temp9(r) {
  return {
    outcomeIndex: r.outcomeIndex,
    outcomeMint: r.outcomeMint,
    balance: r.balance,
    claimRightsBalanceD6: r.claimRightsBalanceD6,
    prizeClaimableD6: r.prizeClaimableD6,
    prizeClaimedD6: r.prizeClaimedD6,
    prizeState: r.prizeState,
    realizedPnl: r.realizedPnlD6
  };
}
function _temp8(outcome) {
  return Number(outcome.outcomeIndex);
}
function _temp7(s_2) {
  return s_2.setHiddenOutcomes;
}
function _temp6(s_1) {
  return s_1.setSelectedOutcome;
}
function _temp5(s_0) {
  return s_0.toggleOutcomeFilter;
}
function _temp4(o_0) {
  return {
    outcomeIndex: Number(o_0.outcomeIndex),
    twarValue: String((o_0 as {
      conviction?: string;
    }).conviction ?? "0")
  };
}
function _temp3(t0) {
  const {
    index,
    mintPubkey
  } = t0;
  return {
    outcomeIndex: index,
    outcomeMint: mintPubkey
  };
}
function _temp2(o) {
  return {
    index: Number(o.outcomeIndex),
    mintPubkey: String((o as {
      mint?: string;
    }).mint ?? "")
  };
}
function MarketResolutionCard(t0) {
  const $ = _c(2);
  const {
    c
  } = t0;
  if (c.liveEvent !== undefined) {
    return null;
  }
  let t1;
  if ($[0] !== c) {
    t1 = <ConnectedMarketResolutionCard c={c} />;
    $[0] = c;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function ConnectedMarketResolutionCard(t0) {
  const $ = _c(7);
  const {
    c
  } = t0;
  const candidate = useAuthoritativePaintedDisplayCandidate();
  const structure = usePaintedMarketShareStructure();
  const authoritative = candidate;
  if (!authoritative) {
    let t1;
    if ($[0] !== c) {
      t1 = <StandaloneMarketResolutionCard c={c} />;
      $[0] = c;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== c || $[3] !== structure) {
    t1 = resolvePaintedResolutionCardState(c, structure);
    $[2] = c;
    $[3] = structure;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const state = t1;
  const t2 = state ?? null;
  let t3;
  if ($[5] !== t2) {
    t3 = <ResolutionCard state={t2} painted={true} />;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function resolvePaintedResolutionCardState(c: MarketDetailModel, structure: PaintedMarketShareStructure): ResolutionCardState | undefined {
  const projected = structure.forecast;
  const outcomeIndex = structure.thresholdReached ? structure.leaderOutcomeIndex : projected?.outcomeIndex ?? null;
  const sourceOutcome = c.tradeFormOutcomes.find(outcome => outcome.index === outcomeIndex);
  const hue = outcomeIndex === null ? undefined : c.outcomeHueByIndex.get(outcomeIndex);
  // A paused session (tab hidden) keeps the frame it last painted, so the card
  // stays put and only its label moves. `inactive`/`pending` have never painted
  // and `error` lost the session: those are the unavailable panel.
  if (outcomeIndex === null || sourceOutcome === undefined || hue === undefined || structure.availability === 'inactive' || structure.availability === 'pending' || structure.availability === 'error' || structure.forecastUpdating) {
    return undefined;
  }
  const outcome = {
    marketPubkey: c.marketPubkey,
    outcomeIndex,
    label: sourceOutcome.label,
    hue,
    imageUrl: c.imageUrlByIndex.get(outcomeIndex) ?? null,
    imageNsfw: c.imageNsfwByIndex.get(outcomeIndex) === true
  };
  // Server-derived threshold and gap: what the card shows until the first
  // painted frame replaces the leaf text.
  if (structure.thresholdReached) {
    return {
      kind: structure.terminalGeometryRevision === null ? 'resolving' : 'graduating',
      outcome,
      threshold: c.thresholdConsumers.resolutionCardThreshold ?? 0,
      crossAtMs: 0
    };
  }
  if (projected === null) return undefined;
  return {
    kind: 'projected',
    outcome,
    gap: c.projectedGap ?? 0,
    crossAtMs: projected.crossAtMs
  };
}
function StandaloneMarketResolutionCard(t0) {
  const $ = _c(20);
  const {
    c
  } = t0;
  const [mountedAtMs] = useState(_temp12);
  const [lastProjectedCrossAtMs, setLastProjectedCrossAtMs] = useState(null);
  const [resolvingSinceMs, setResolvingSinceMs] = useState(null);
  const resolvingCrossAtMs = Math.min(lastProjectedCrossAtMs ?? Number.POSITIVE_INFINITY, resolvingSinceMs ?? mountedAtMs);
  let t1;
  if ($[0] !== c.imageNsfwByIndex || $[1] !== c.imageUrlByIndex || $[2] !== c.liveConviction || $[3] !== c.marketPubkey || $[4] !== c.marketStatus || $[5] !== c.outcomeHueByIndex || $[6] !== c.outcomes || $[7] !== c.pipelineStage || $[8] !== c.projectedGap || $[9] !== c.projectedWinner || $[10] !== c.resolutionHolding || $[11] !== c.thresholdConsumers.resolutionCardThreshold || $[12] !== resolvingCrossAtMs) {
    t1 = resolveResolutionCardState({
      marketPubkey: c.marketPubkey,
      marketStatus: c.marketStatus,
      pipelineStage: c.pipelineStage,
      outcomes: c.outcomes,
      outcomeHueByIndex: c.outcomeHueByIndex,
      imageUrlByIndex: c.imageUrlByIndex,
      imageNsfwByIndex: c.imageNsfwByIndex,
      liveConviction: c.liveConviction,
      threshold: c.thresholdConsumers.resolutionCardThreshold,
      projectedWinner: c.projectedWinner,
      projectedGap: c.projectedGap,
      resolvingCrossAtMs,
      resolutionHolding: c.resolutionHolding
    });
    $[0] = c.imageNsfwByIndex;
    $[1] = c.imageUrlByIndex;
    $[2] = c.liveConviction;
    $[3] = c.marketPubkey;
    $[4] = c.marketStatus;
    $[5] = c.outcomeHueByIndex;
    $[6] = c.outcomes;
    $[7] = c.pipelineStage;
    $[8] = c.projectedGap;
    $[9] = c.projectedWinner;
    $[10] = c.resolutionHolding;
    $[11] = c.thresholdConsumers.resolutionCardThreshold;
    $[12] = resolvingCrossAtMs;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  const state = t1;
  const kind = state?.kind ?? null;
  const projectedCrossAtMs = state?.kind === "projected" ? state.crossAtMs : null;
  let t2;
  let t3;
  if ($[14] !== kind || $[15] !== projectedCrossAtMs) {
    t2 = () => {
      if (kind === "projected") {
        setLastProjectedCrossAtMs(projectedCrossAtMs);
      }
      if (kind === "resolving" || kind === "graduating") {
        setResolvingSinceMs(_temp13);
      } else {
        setResolvingSinceMs(null);
      }
    };
    t3 = [kind, projectedCrossAtMs];
    $[14] = kind;
    $[15] = projectedCrossAtMs;
    $[16] = t2;
    $[17] = t3;
  } else {
    t2 = $[16];
    t3 = $[17];
  }
  useEffect(t2, t3);
  let t4;
  if ($[18] !== state) {
    t4 = <ResolutionCard state={state} />;
    $[18] = state;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return t4;
}

// ---------------------------------------------------------------------------
// Right column — desktop only, sticky TradeForm / TokenCta + position + prize pool
// ---------------------------------------------------------------------------
function _temp13(since) {
  return since ?? Date.now();
}
function _temp12() {
  return Date.now();
}
function MarketDetailRightColumnTop(t0) {
  const $ = _c(40);
  const {
    c
  } = t0;
  const {
    outcomes,
    isPostResolution,
    winnerOutcomeIdx,
    marketStatus,
    marketPubkey,
    winnerName,
    winnerTicker,
    winnerImageUrl,
    winnerImageNsfw,
    winnerMint,
    tradeFormOutcomes,
    hasHoldings,
    market,
    selectedOutcomeIndex,
    handleOutcomeChange
  } = c;
  if (outcomes === undefined) {
    return null;
  }
  if (isPostResolution) {
    if (winnerOutcomeIdx === null) {
      return null;
    }
    let t1;
    let t2;
    if ($[0] !== marketPubkey || $[1] !== selectedOutcomeIndex) {
      t1 = <DirectTokenPurchaseAction marketAddress={marketPubkey} outcomeIndex={selectedOutcomeIndex} origin="market" />;
      t2 = <DirectTokenPurchaseAction placement="responsive" marketAddress={marketPubkey} outcomeIndex={selectedOutcomeIndex} origin="market" />;
      $[0] = marketPubkey;
      $[1] = selectedOutcomeIndex;
      $[2] = t1;
      $[3] = t2;
    } else {
      t1 = $[2];
      t2 = $[3];
    }
    const t3 = marketStatus === "graduated" ? "live" : "pending";
    let t4;
    if ($[4] !== winnerMint) {
      t4 = winnerMint === "" ? {} : {
        mint: winnerMint
      };
      $[4] = winnerMint;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== marketPubkey || $[7] !== t3 || $[8] !== t4 || $[9] !== winnerImageNsfw || $[10] !== winnerImageUrl || $[11] !== winnerName || $[12] !== winnerOutcomeIdx || $[13] !== winnerTicker) {
      t5 = <TokenCta variant={t3} marketPubkey={marketPubkey} winnerOutcomeIndex={winnerOutcomeIdx} name={winnerName} ticker={winnerTicker} imageUrl={winnerImageUrl} imageNsfw={winnerImageNsfw} {...t4} />;
      $[6] = marketPubkey;
      $[7] = t3;
      $[8] = t4;
      $[9] = winnerImageNsfw;
      $[10] = winnerImageUrl;
      $[11] = winnerName;
      $[12] = winnerOutcomeIdx;
      $[13] = winnerTicker;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    let t6;
    if ($[15] !== t1 || $[16] !== t2 || $[17] !== t5) {
      t6 = <div className="flex flex-col gap-4">{t1}{t2}{t5}</div>;
      $[15] = t1;
      $[16] = t2;
      $[17] = t5;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    return t6;
  }
  let t1;
  if ($[19] !== marketPubkey || $[20] !== selectedOutcomeIndex) {
    t1 = <DirectTokenPurchaseAction marketAddress={marketPubkey} outcomeIndex={selectedOutcomeIndex} origin="market" />;
    $[19] = marketPubkey;
    $[20] = selectedOutcomeIndex;
    $[21] = t1;
  } else {
    t1 = $[21];
  }
  let t2;
  if ($[22] !== market) {
    t2 = market?.title === undefined ? {} : {
      marketTitle: market.title
    };
    $[22] = market;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  let t3;
  if ($[24] !== selectedOutcomeIndex) {
    t3 = selectedOutcomeIndex === null ? {} : {
      selectedOutcomeIndex
    };
    $[24] = selectedOutcomeIndex;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  let t4;
  if ($[26] !== marketStatus) {
    t4 = isValidTradeFormStatus(marketStatus) ? {
      marketStatus
    } : {};
    $[26] = marketStatus;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  const t5 = c.resolutionHolding ? market : undefined;
  let t6;
  if ($[28] !== handleOutcomeChange || $[29] !== hasHoldings || $[30] !== marketPubkey || $[31] !== t2 || $[32] !== t3 || $[33] !== t4 || $[34] !== t5 || $[35] !== tradeFormOutcomes) {
    t6 = <ConnectedMarketTradeForm marketAddress={marketPubkey} outcomes={tradeFormOutcomes} hasHoldings={hasHoldings} {...t2} {...t3} {...t4} heldMarket={t5} onOutcomeChange={handleOutcomeChange} />;
    $[28] = handleOutcomeChange;
    $[29] = hasHoldings;
    $[30] = marketPubkey;
    $[31] = t2;
    $[32] = t3;
    $[33] = t4;
    $[34] = t5;
    $[35] = tradeFormOutcomes;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  let t7;
  if ($[37] !== t1 || $[38] !== t6) {
    t7 = <div id="market-trade-form" tabIndex={-1}>{t1}{t6}</div>;
    $[37] = t1;
    $[38] = t6;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  return t7;
}
function MarketDetailRightColumnPosition(t0) {
  const $ = _c(6);
  const {
    c
  } = t0;
  let t1;
  if ($[0] !== c.onchainTradeData.data) {
    t1 = c.onchainTradeData.data === undefined ? {} : {
      tradeData: c.onchainTradeData.data
    };
    $[0] = c.onchainTradeData.data;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== c.marketPubkey || $[3] !== c.positionRows || $[4] !== t1) {
    t2 = <MarketPositionPanel marketPubkey={c.marketPubkey} positionRows={c.positionRows} {...t1} />;
    $[2] = c.marketPubkey;
    $[3] = c.positionRows;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function MarketDetailRightColumnRewards(t0) {
  const $ = _c(2);
  const {
    c,
    placement
  } = t0;
  if (!c.hasBackerRewards || placement !== "below-prize-pool") {
    return null;
  }
  let t1;
  if ($[0] !== c.backerRewards) {
    t1 = <BackerRewardsCardView {...c.backerRewards} className="max-w-none" />;
    $[0] = c.backerRewards;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function MarketDetailRightColumn(t0) {
  const $ = _c(18);
  const {
    c
  } = t0;
  const {
    backerRewards,
    marketStatus,
    marketPubkey
  } = c;
  let t1;
  if ($[0] !== backerRewards.state) {
    t1 = resolveBackerRewardsPlacement(backerRewards.state);
    $[0] = backerRewards.state;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rewardsPlacement = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn(APP_HEADER_STICKY_TOP_SIDEBAR_CLASS, DETAIL_SIDEBAR_W_CLASS, "sticky hidden min-h-fit shrink-0 self-start lg:flex lg:flex-col");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] !== c) {
    t3 = <MarketDetailRightColumnTop c={c} />;
    t4 = <MarketDetailRightColumnPosition c={c} />;
    $[3] = c;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== marketPubkey || $[7] !== marketStatus) {
    t5 = <PrizePool.Card marketPubkey={marketPubkey} marketStatus={marketStatus} />;
    $[6] = marketPubkey;
    $[7] = marketStatus;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== c || $[10] !== rewardsPlacement) {
    t6 = <MarketDetailRightColumnRewards c={c} placement={rewardsPlacement} />;
    $[9] = c;
    $[10] = rewardsPlacement;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== c.isPostResolution || $[13] !== t3 || $[14] !== t4 || $[15] !== t5 || $[16] !== t6) {
    t7 = <div className={t2}><MarketDetailSidebar postResolution={c.isPostResolution} top={t3} positions={t4} prizePool={t5} rewards={t6} /></div>;
    $[12] = c.isPostResolution;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Market detail page orchestrator — `'use client'` boundary.
 *
 * Fetches all market data via SDK hooks and distributes it to child
 * components. Handles responsive layout (desktop 2-col, tablet/mobile 1-col).
 */
export function MarketDetailLayoutView(props) {
  const $ = _c(80);
  const c = useMarketDetailLayout(props);
  const {
    marketPubkey,
    isDemoMode,
    marketIsPending,
    market,
    outcomes,
    marketStatus,
    hasHoldings,
    prizePool,
    prizePoolTotalUnavailable,
    payoutEstimates,
    payoutEstimatesPending,
    isPostResolution,
    winningOutcomeLabel,
    mobileTradeOutcomes,
    handleOutcomeChange
  } = c;
  const t0 = market !== undefined;
  let t1;
  if ($[0] !== marketPubkey || $[1] !== t0) {
    t1 = {
      name: "market_detail",
      isReady: t0,
      instanceKey: marketPubkey
    };
    $[0] = marketPubkey;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useRoutePerf(t1);
  if (isDemoMode && !marketIsPending && market === undefined) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <DemoNotFoundCard kind="market" />;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  const t2 = !c.holdingsQuerySettled || prizePoolTotalUnavailable;
  let t3;
  if ($[4] !== market || $[5] !== marketPubkey) {
    t3 = <StageOverrideMount pubkey={marketPubkey} market={market} />;
    $[4] = market;
    $[5] = marketPubkey;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== c) {
    t4 = <MarketResolutionGlow c={c} />;
    $[7] = c;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = outcomes !== undefined && !isPostResolution;
  let t6;
  if ($[9] !== market) {
    t6 = market?.title !== undefined ? {
      marketTitle: market.title
    } : {};
    $[9] = market;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = c.resolutionHolding ? market : undefined;
  let t8;
  if ($[11] !== c || $[12] !== props.chartOpening || $[13] !== props.tradesFrom) {
    t8 = <MarketDetailChartHeader c={c} tradesFrom={props.tradesFrom} chartOpening={props.chartOpening} />;
    $[11] = c;
    $[12] = props.chartOpening;
    $[13] = props.tradesFrom;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t10;
  let t11;
  let t12;
  let t13;
  let t9;
  if ($[15] !== c) {
    t9 = <MarketDetailLiveEventTimeCard c={c} />;
    t10 = <MarketResolutionCard c={c} />;
    t11 = <MarketDetailOutcomes c={c} />;
    t12 = <MarketDetailMobileCtas c={c} />;
    t13 = <MarketDetailMobilePositionAndPrizePool c={c} />;
    $[15] = c;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
    $[20] = t9;
  } else {
    t10 = $[16];
    t11 = $[17];
    t12 = $[18];
    t13 = $[19];
    t9 = $[20];
  }
  let t14;
  if ($[21] !== c.market) {
    t14 = <MarketIdentitySection identity={c.market} />;
    $[21] = c.market;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== c) {
    t15 = <MarketDetailTabs c={c} />;
    $[23] = c;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== marketPubkey) {
    t16 = <SimilarMarketsSection marketPubkey={marketPubkey} />;
    $[25] = marketPubkey;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== isPostResolution || $[28] !== outcomes) {
    t17 = outcomes !== undefined && !isPostResolution && <div aria-hidden={true} className="h-20 lg:hidden" />;
    $[27] = isPostResolution;
    $[28] = outcomes;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t10 || $[31] !== t11 || $[32] !== t12 || $[33] !== t13 || $[34] !== t14 || $[35] !== t15 || $[36] !== t16 || $[37] !== t17 || $[38] !== t8 || $[39] !== t9) {
    t18 = <div className="flex min-w-0 flex-1 flex-col gap-4">{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}</div>;
    $[30] = t10;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
    $[38] = t8;
    $[39] = t9;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== c) {
    t19 = <MarketDetailRightColumn c={c} />;
    $[41] = c;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t18 || $[44] !== t19) {
    t20 = <div className="flex gap-6">{t18}{t19}</div>;
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <MarketDetailDialogs />;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t20) {
    t22 = <div>{t20}{t21}</div>;
    $[47] = t20;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== handleOutcomeChange || $[50] !== hasHoldings || $[51] !== marketPubkey || $[52] !== marketStatus || $[53] !== mobileTradeOutcomes || $[54] !== t22 || $[55] !== t5 || $[56] !== t6 || $[57] !== t7) {
    t23 = <MarketMobileTradeRoot enabled={t5} marketPubkey={marketPubkey} {...t6} outcomes={mobileTradeOutcomes} marketStatus={marketStatus} heldMarket={t7} hasHoldings={hasHoldings} onOutcomeChange={handleOutcomeChange}>{t22}</MarketMobileTradeRoot>;
    $[49] = handleOutcomeChange;
    $[50] = hasHoldings;
    $[51] = marketPubkey;
    $[52] = marketStatus;
    $[53] = mobileTradeOutcomes;
    $[54] = t22;
    $[55] = t5;
    $[56] = t6;
    $[57] = t7;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== c.marketStatus || $[60] !== marketPubkey || $[61] !== t23 || $[62] !== t3 || $[63] !== t4) {
    t24 = <PaintedMarketShareDisplayProvider marketPubkey={marketPubkey} lifecycleStatus={c.marketStatus}>{t3}{t4}{t23}</PaintedMarketShareDisplayProvider>;
    $[59] = c.marketStatus;
    $[60] = marketPubkey;
    $[61] = t23;
    $[62] = t3;
    $[63] = t4;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== payoutEstimates || $[66] !== payoutEstimatesPending || $[67] !== prizePool.claim || $[68] !== prizePool.hasClaimed || $[69] !== prizePool.isClaimLoading || $[70] !== prizePool.isLoser || $[71] !== prizePool.isWinner || $[72] !== prizePool.locked || $[73] !== prizePool.prizePoolAmount || $[74] !== prizePool.userShareD6 || $[75] !== prizePool.userSharePct || $[76] !== t2 || $[77] !== t24 || $[78] !== winningOutcomeLabel) {
    t25 = <TradeExecutorProvider><PrizePool.Provider amount={prizePool.prizePoolAmount} viewerStatePending={t2} locked={prizePool.locked} isWinner={prizePool.isWinner} isLoser={prizePool.isLoser} userShareD6={prizePool.userShareD6} userSharePct={prizePool.userSharePct} payoutEstimates={payoutEstimates} payoutEstimatesPending={payoutEstimatesPending} winningOutcomeLabel={winningOutcomeLabel} claimed={prizePool.hasClaimed} claimInFlight={prizePool.isClaimLoading} onClaim={prizePool.claim}>{t24}</PrizePool.Provider></TradeExecutorProvider>;
    $[65] = payoutEstimates;
    $[66] = payoutEstimatesPending;
    $[67] = prizePool.claim;
    $[68] = prizePool.hasClaimed;
    $[69] = prizePool.isClaimLoading;
    $[70] = prizePool.isLoser;
    $[71] = prizePool.isWinner;
    $[72] = prizePool.locked;
    $[73] = prizePool.prizePoolAmount;
    $[74] = prizePool.userShareD6;
    $[75] = prizePool.userSharePct;
    $[76] = t2;
    $[77] = t24;
    $[78] = winningOutcomeLabel;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  return t25;
}
function MarketDetailChartHeader(t0) {
  const $ = _c(35);
  const {
    c,
    tradesFrom,
    chartOpening
  } = t0;
  const chartReady = useMarketDetailChartDefault(c.marketPubkey);
  const t1 = c.market?.title ?? "";
  const t2 = c.market?.ticker as string | null | undefined ?? null;
  const t3 = c.market?.coverImageUrl ?? null;
  const t4 = c.market?.coverImageNsfw ?? false;
  const t5 = c.market?.category as string | null ?? null;
  const t6 = String(c.market?.createdAt ?? "");
  const t7 = String(c.market?.volumeTotal ?? "0");
  let t8;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4 || $[4] !== t5 || $[5] !== t6 || $[6] !== t7) {
    t8 = {
      title: t1,
      ticker: t2,
      imageUrl: t3,
      imageNsfw: t4,
      category: t5,
      createdAt: t6,
      volumeTotal: t7
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const headerMarket = t8;
  let t9;
  if ($[8] !== c.marketStatus || $[9] !== c.winnerName || $[10] !== c.winnerOutcomeIdx) {
    t9 = c.marketStatus === "resolved" && c.winnerOutcomeIdx !== null ? <GraduatingBanner name={c.winnerName} /> : null;
    $[8] = c.marketStatus;
    $[9] = c.winnerName;
    $[10] = c.winnerOutcomeIdx;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== c.liveEvent || $[13] !== c.market || $[14] !== c.marketPubkey || $[15] !== c.marketStatus || $[16] !== c.shareCanonicalPath || $[17] !== headerMarket) {
    t10 = c.market === undefined ? <MarketHeaderSkeleton /> : c.liveEvent === undefined ? <MarketHeader marketType={c.market.marketType} market={headerMarket} marketPubkey={c.marketPubkey} canonicalPath={c.shareCanonicalPath} /> : <MarketHeader marketType={c.market.marketType} market={{
      ...headerMarket,
      kind: "live"
    }} liveEvent={c.liveEvent.event} marketPubkey={c.marketPubkey} presentedStatus={c.marketStatus} canonicalPath={c.shareCanonicalPath} />;
    $[12] = c.liveEvent;
    $[13] = c.market;
    $[14] = c.marketPubkey;
    $[15] = c.marketStatus;
    $[16] = c.shareCanonicalPath;
    $[17] = headerMarket;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== c.marketPubkey || $[20] !== c.marketStatus || $[21] !== c.resolutionHolding || $[22] !== c.thresholdConsumers || $[23] !== chartOpening || $[24] !== chartReady || $[25] !== tradesFrom) {
    t11 = chartReady ? <ConnectedChartContainer marketPubkey={c.marketPubkey} lifecycleStatus={c.marketStatus} resolutionHolding={c.resolutionHolding} {...c.thresholdConsumers.chartResolutionThreshold !== undefined ? {
      resolutionThreshold: c.thresholdConsumers.chartResolutionThreshold
    } : {}} opening={chartOpening} tradesFrom={tradesFrom} className="w-full" /> : null;
    $[19] = c.marketPubkey;
    $[20] = c.marketStatus;
    $[21] = c.resolutionHolding;
    $[22] = c.thresholdConsumers;
    $[23] = chartOpening;
    $[24] = chartReady;
    $[25] = tradesFrom;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11) {
    t12 = <div className="relative w-full">{t11}</div>;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t12) {
    t13 = <Panel density="compact" inheritBackground={true} surface="none" className="min-w-0 gap-4 rounded-none py-0">{t10}{t12}</Panel>;
    $[29] = t10;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13 || $[33] !== t9) {
    t14 = <>{t9}{t13}</>;
    $[32] = t13;
    $[33] = t9;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
}
function MarketDetailLiveEventTimeCard(t0) {
  const $ = _c(2);
  const {
    c
  } = t0;
  if (c.liveEvent === undefined || c.marketStatus === "resolved" || c.marketStatus === "graduated") {
    return null;
  }
  let t1;
  if ($[0] !== c.liveEvent) {
    t1 = <EventTimeCard variant="live" {...c.liveEvent} />;
    $[0] = c.liveEvent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function marketDetailOutcomeRows(c: MarketDetailModel): ComponentProps<typeof ConnectedMarketOutcomesOverviewPanel>['rows'] {
  return c.tradeFormOutcomes.map(outcome => {
    const conviction = c.liveConviction.size === 0 ? undefined : c.liveConviction.get(outcome.index);
    return {
      index: outcome.index,
      label: outcome.label,
      priceD9: c.outcomeSpotPriceD9ByIndex.get(outcome.index) ?? null,
      ...(conviction !== undefined && Number.isFinite(conviction) ? {
        conviction
      } : {}),
      imageUrl: outcome.imageUrl,
      imageNsfw: outcome.imageNsfw
    };
  });
}
function MarketDetailOutcomes(t0) {
  const $ = _c(9);
  const {
    c
  } = t0;
  if (c.tradeFormOutcomes.length === 0 || c.marketStatus === "resolved" || c.marketStatus === "graduated") {
    return null;
  }
  const t1 = c.marketPubkey;
  const t2 = c.market?.category as string | null ?? null;
  const t3 = c.handleChartOutcomeChange;
  const t4 = c.thresholdConsumers.goalPercent;
  let t5;
  if ($[0] !== c) {
    t5 = marketDetailOutcomeRows(c);
    $[0] = c;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== c.handleChartOutcomeChange || $[3] !== c.marketPubkey || $[4] !== c.resolutionHolding || $[5] !== c.thresholdConsumers.goalPercent || $[6] !== t2 || $[7] !== t5) {
    t6 = <ConnectedMarketOutcomesOverviewPanel marketPubkey={t1} marketCategory={t2} onOutcomeChange={t3} goalPercent={t4} rows={t5} resolutionHolding={c.resolutionHolding} />;
    $[2] = c.handleChartOutcomeChange;
    $[3] = c.marketPubkey;
    $[4] = c.resolutionHolding;
    $[5] = c.thresholdConsumers.goalPercent;
    $[6] = t2;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
function MarketDetailMobileCtas(t0) {
  const $ = _c(12);
  const {
    c
  } = t0;
  if (c.outcomes === undefined) {
    return null;
  }
  if (!c.isPostResolution) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <MarketMobileTradeStickyAnchor />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (c.winnerOutcomeIdx === null) {
    return null;
  }
  const t1 = c.marketStatus === "graduated" ? "live" : "pending";
  let t2;
  if ($[1] !== c.winnerMint) {
    t2 = c.winnerMint !== "" ? {
      mint: c.winnerMint
    } : {};
    $[1] = c.winnerMint;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== c.marketPubkey || $[4] !== c.winnerImageNsfw || $[5] !== c.winnerImageUrl || $[6] !== c.winnerName || $[7] !== c.winnerOutcomeIdx || $[8] !== c.winnerTicker || $[9] !== t1 || $[10] !== t2) {
    t3 = <div className="lg:hidden"><TokenCta variant={t1} marketPubkey={c.marketPubkey} winnerOutcomeIndex={c.winnerOutcomeIdx} name={c.winnerName} ticker={c.winnerTicker} imageUrl={c.winnerImageUrl} imageNsfw={c.winnerImageNsfw} {...t2} /></div>;
    $[3] = c.marketPubkey;
    $[4] = c.winnerImageNsfw;
    $[5] = c.winnerImageUrl;
    $[6] = c.winnerName;
    $[7] = c.winnerOutcomeIdx;
    $[8] = c.winnerTicker;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function MarketDetailMobilePositionAndPrizePool(t0) {
  const $ = _c(21);
  const {
    c
  } = t0;
  let t1;
  if ($[0] !== c.backerRewards.state) {
    t1 = resolveBackerRewardsPlacement(c.backerRewards.state);
    $[0] = c.backerRewards.state;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rewardsPlacement = t1;
  let t2;
  if ($[2] !== c.onchainTradeData.data) {
    t2 = c.onchainTradeData.data === undefined ? {} : {
      tradeData: c.onchainTradeData.data
    };
    $[2] = c.onchainTradeData.data;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== c.marketPubkey || $[5] !== c.positionRows || $[6] !== t2) {
    t3 = <MarketPositionPanel marketPubkey={c.marketPubkey} positionRows={c.positionRows} {...t2} className="max-w-none lg:hidden" />;
    $[4] = c.marketPubkey;
    $[5] = c.positionRows;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== c.marketPubkey || $[9] !== c.marketStatus) {
    t4 = <PrizePool.Card marketPubkey={c.marketPubkey} marketStatus={c.marketStatus} className="max-w-none" />;
    $[8] = c.marketPubkey;
    $[9] = c.marketStatus;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== c.backerRewards || $[12] !== c.hasBackerRewards || $[13] !== rewardsPlacement) {
    t5 = c.hasBackerRewards && rewardsPlacement === "below-prize-pool" ? <BackerRewardsCardView {...c.backerRewards} className="max-w-none" /> : null;
    $[11] = c.backerRewards;
    $[12] = c.hasBackerRewards;
    $[13] = rewardsPlacement;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = <div className="flex flex-col gap-3 lg:hidden">{t4}{t5}</div>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t3 || $[19] !== t6) {
    t7 = <>{t3}{t6}</>;
    $[18] = t3;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}
function MarketDetailDialogs() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><ThesisDialog /><PrizePool.HowItWorksModal /></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export { MarketDetailLayout } from '@/components/market/market-detail-data-boundary';